import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { getAiBaseUrl, getAiGeminiUrl, getAiRevampUrl } from "../api/configHelper";
import { Code, FileSpreadsheet, Eye, Loader, Bot, Send, Plus, Mic, ChevronDown, ChevronUp, Maximize2, Minimize2 } from "lucide-react";
import * as XLSX from 'xlsx';
import { getPermissions } from "../api/configHelper";


// -----------------------------
// AdvanceReport.js (final)
// -----------------------------

// Helper: read current user and sessId from sessionStorage (fallbacks)
const getCurrentUser = () => sessionStorage.getItem("userName") || "admin";
const getSessId = () => sessionStorage.getItem("SessId") || sessionStorage.getItem("logId") || "admin";

// -----------------------------
// DataTable Component
// -----------------------------
const DataTable = ({ data }) => {
  const [filters, setFilters] = React.useState({});
  const [search, setSearch] = React.useState("");

  if (!data || data.length === 0)
    return <div className="p-2 text-gray-500">No data</div>;

  const headers = Object.keys(data[0]);

  const filteredData = data.filter((row) => {
    if (search) {
      const rowStr = Object.values(row).join(" ").toLowerCase();
      if (!rowStr.includes(search.toLowerCase())) return false;
    }
    for (const h of headers) {
      if (
        filters[h] &&
        !String(row[h]).toLowerCase().includes(filters[h].toLowerCase())
      )
        return false;
    }
    return true;
  });

   return (
     <div className="w-full overflow-auto rounded-lg shadow-sm" style={{ backgroundColor: 'var(--base-100)', borderColor: 'var(--base-300)', maxHeight: '100%' }}>
      <div
  className="relative"
  style={{
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: "var(--base-100)"
  }}
>
  <table className="excel-table min-w-[1600px]">
    <thead>
      <tr
        className="sticky top-0 z-20"
        style={{ backgroundColor: "var(--base-200)" }}
      >
        {headers.map((h) => (
          <th
            key={h}
            className="px-2 py-1 text-xs font-semibold border-b"
            style={{ borderColor: "var(--base-300)" }}
          >
            {h}
          </th>
        ))}
      </tr>

      <tr
        className="sticky top-[28px] z-20"
        style={{ backgroundColor: "var(--base-100)" }}
      >
        {headers.map((h) => (
          <th key={h} className="p-1 border-b">
            <input
              className="w-full px-1 py-1 text-xs border rounded"
              placeholder="Filter"
              value={filters[h] || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, [h]: e.target.value }))
              }
            />
          </th>
        ))}
      </tr>
    </thead>


        <tbody>
          {filteredData.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-b hover:transition-colors"
              style={{
                backgroundColor: rowIndex % 2 === 0 ? 'var(--base-100)' : 'var(--base-200)',
                borderColor: 'var(--base-300)',
                borderBottom: '1px solid var(--base-300)'
              }}
              onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
              onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = rowIndex % 2 === 0 ? 'var(--base-100)' : 'var(--base-200)'}
            >
              {headers.map((h) => (
                <td
                  key={h}
                  className="px-2 py-1 text-sm border-r"
                  style={{ borderRight: '1px solid var(--base-300)', color: 'var(--base-content)', fontFamily: 'monospace', fontSize: '12px' }}
                >
                  {row[h]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
};

// -----------------------------
// TabsComponent
// -----------------------------
const TabsComponent = ({ tables }) => {
  const table2 = tables?.Table2 || [];
  const tabs = table2.map(item => ({
    name: item.TblName,
    tableKey: `Table${2 + parseInt(item.TblNo)}`,
    data: tables[`Table${2 + parseInt(item.TblNo)}`] || []
  })).filter(tab => tab.data.length > 0);
  const [activeTab, setActiveTab] = React.useState(tabs[0]?.name || '');
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: 'var(--base-100)' }}>
        <div className="flex items-center justify-between border-b p-" style={{ backgroundColor: 'var(--base-200)', borderColor: 'var(--base-300)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--base-content)' }}>{activeTab}</h3>
          <button onClick={() => setIsExpanded(false)} className="p-2 rounded hover:bg-gray-200" title="Minimize" style={{ backgroundColor: 'var(--base-100)', color: 'var(--base-content)' }}>
            <Minimize2 size={20} />
          </button>
        </div>
      <div className="flex-1 p-2 overflow-auto custom-scrollbar" style={{
        scrollbarWidth: 'thick',
        scrollbarColor: 'var(--base-content) var(--base-200)'
      }}>
        <style>
          {`
            .custom-scrollbar::-webkit-scrollbar {
              width: 16px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: var(--base-200);
              border-radius: 8px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: var(--base-content);
              border-radius: 8px;
              border: 2px solid var(--base-200);
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: var(--primary);
            }
          `}
        </style>
          <DataTable data={tabs.find(t => t.name === activeTab)?.data || []} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex border-b">
        {tabs.map(tab => (
          <button key={tab.name} className={`px-3 py-1 text-sm ${activeTab === tab.name ? 'border-b-2 border-blue-500 font-semibold' : ''}`} onClick={() => setActiveTab(tab.name)}>
            {tab.name}
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <button onClick={() => setIsExpanded(true)} className="p-1 rounded" title="Expand Table" style={{ backgroundColor: 'var(--base-100)', color: 'var(--base-content)' }}>
          <Maximize2 size={18} />
        </button>
      </div>
      {activeTab && <DataTable data={tabs.find(t => t.name === activeTab)?.data || []} />}
    </div>
  );
};

// -----------------------------
// SearchableSelect (single-select with inline search)
// -----------------------------
const SearchableSelect = ({ options, value, onChange, placeholder, isLoading, autoFocus = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");
  const [selectedIndex, setSelectedIndex] = React.useState(-1);

  React.useEffect(() => {
    if (value) {
      const sel = options.find((o) => o.value == value);
      setInputValue(sel ? sel.label : "");
    } else {
      setInputValue("");
    }
  }, [value, options]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setSearchTerm(e.target.value);
    setSelectedIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (opt) => {
    onChange(opt.value);
    setInputValue(opt.label);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleFocus = () => {
    setSearchTerm(inputValue);
    setSelectedIndex(-1);
  };

  const handleBlur = () => {
    // small delay to allow click on option
    setTimeout(() => setIsOpen(false), 150);
  };

  const filtered = options.filter((o) =>
    String(o.label).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <input
        type="text"
        className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
            return;
          }
          if (!isOpen) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, -1));
          } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            handleSelect(filtered[selectedIndex]);
          }
        }}
        disabled={isLoading}
        autoFocus={autoFocus}
      />

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 overflow-auto bg-white border rounded-md shadow-lg border-base-300 max-h-60">
          {filtered.length > 0 ? (
            filtered.map((opt, idx) => (
              <div
                key={idx}
                onMouseDown={() => handleSelect(opt)} // use onMouseDown to avoid blur race
                className={`px-4 py-2 cursor-pointer ${selectedIndex === idx ? 'bg-blue-200' : 'hover:bg-base-200'}`}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500">No matching options</div>
          )}
        </div>
      )}
    </div>
  );
};

// -----------------------------
// ModalMultiSelect (opaque white modal; uses Table3 for options)
// -----------------------------
const ModalMultiSelect = ({ options, value, onChange, title, isLoading }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const toggleOption = (optValue) => {
    const currentValues = Array.isArray(value) ? value : [];
    let newValues;
    if (optValue === "All") {
      newValues = currentValues.includes("All") ? [] : ["All"];
    } else {
      let temp = currentValues.filter((v) => v !== "All");
      newValues = temp.includes(optValue) ? temp.filter((v) => v !== optValue) : [...temp, optValue];
    }
    onChange(newValues);
  };

  const getDisplayText = () => {
    if (isLoading) return "Loading options...";
    if (!value || value.length === 0) return "Select options...";
    if (value.includes("All")) return "All Records Selected";
    const labels = options.filter((o) => value.includes(o.value)).map((o) => o.label);
    if (labels.length > 2) return `${labels.length} items selected`;
    return labels.join(", ");
  };

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <div
        onClick={() => !isLoading && setIsOpen(true)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className="text-sm truncate">{getDisplayText()}</span>
        <span className="text-xs text-gray-500">▼</span>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[80vh] flex flex-col border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
              <button className="text-xl text-gray-500 hover:text-gray-700" onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>

            <input
              className="w-full px-3 py-2 mb-4 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />

            <div className="flex-1 pr-1 space-y-1 overflow-y-auto">
              {filtered.length > 0 ? (
                filtered.map((opt, idx) => (
                  <label
                    key={idx}
                    className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-gray-50"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      toggleOption(opt.value);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={Array.isArray(value) && value.includes(opt.value)}
                      readOnly
                    />
                    <span className="flex-1 text-sm">{opt.label}</span>
                    <span className="text-xs text-gray-500">{opt.value}</span>
                  </label>
                ))
              ) : (
                <div className="py-8 text-sm text-center text-gray-500">No options found</div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => setIsOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------
// Main ReportGenerator (uses reportsApi)
// -----------------------------
const ReportGenerator = () => {
  const navigate = useNavigate();
  const [reportList, setReportList] = React.useState([]);
  const [reportConfig, setReportConfig] = React.useState(null); // { id, parameters: [...] }
  const [dropdownOptions, setDropdownOptions] = React.useState({}); // keyed by paramName
  const [selectedReportId, setSelectedReportId] = React.useState("");
  const [formValues, setFormValues] = React.useState({});
  const [outputModal, setOutputModal] = React.useState({ show: false, excel: "", pdf: "" });

  const [loadingReports, setLoadingReports] = React.useState(true);
  const [loadingParams, setLoadingParams] = React.useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [reportData, setReportData] = React.useState(null);
  const [userParamList, setUserParamList] = React.useState('');
  const [showAiChat, setShowAiChat] = React.useState(false);
  const [aiMessages, setAiMessages] = React.useState([]);
  const [aiInputText, setAiInputText] = React.useState("");
  const [aiAttachments, setAiAttachments] = React.useState([]);
  const [aiIsLoading, setAiIsLoading] = React.useState(false);
  const [aiIsListening, setAiIsListening] = React.useState(false);
  const [selectedAiEndpoint, setSelectedAiEndpoint] = React.useState("AI_BASE_URL");
  const aiFileInputRef = React.useRef(null);
  const aiMessagesEndRef = React.useRef(null);

  // Load report list from API
  React.useEffect(() => {
    const load = async () => {
      try {
        setLoadingReports(true);
        const user = getCurrentUser();
        const resp = await api.AdvRptGetReportList(user);
        // API returns { Table: [...] }
        const reports = resp?.Table || [];
        // map to id/name
        const mapped = reports.map((r) => ({ id: r.Rptid ?? r.RptId ?? r.rptid, name: r.Reportname ?? r.Reportname ?? r.ReportName }));
        setReportList(mapped);
      } catch (err) {
        console.error("Failed to load report list", err);
      } finally {
        setLoadingReports(false);
      }
    };
    load();
  }, []);

  // Scroll to bottom for AI messages
  const scrollAiToBottom = () => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollAiToBottom();
  }, [aiMessages]);

  // When user selects a report, call AdvRpt-GetUserControls
  const handleReportSelect = async (rptId) => {
    setSelectedReportId(rptId);
    setReportConfig(null);
    setFormValues({});
    setDropdownOptions({});

    if (!rptId) return;

    try {
      setLoadingParams(true);
      const user = getCurrentUser();
      const sessId = getSessId();
      const resp = await api.AdvRptGetUserControls(user, rptId, sessId);
      const rptMeta = resp?.Table?.[0] || resp?.GetRefreshData?.Table?.[0] || null;
      const params = (resp?.Table1 || []).map((p) => ({
        slNo: p.SlNo,
        title: p.ParamTitle || p.ParamName || p.Param,
        name: p.ParamName,
        controlType: p.ControlType,
        defaultValue: p.DefaultValue,
        tblName: p.Tblname,
        primeValue: p.Primevalue,
        userValue: p.UserValue,
        addlColumns: p.addlColumns,
        isMultiSelect: p.IsMultiSelect,
      }));

      // initialize form values
      const initial = {};
      params.forEach((p) => {
        if (p.controlType?.toLowerCase() === "datetime") {
          // set all date fields to current date
          initial[p.name] = new Date().toISOString().split("T")[0];
        } else if (p.controlType?.toLowerCase() === "dropdown") {
          if (p.isMultiSelect === 1) {
            initial[p.name] = p.defaultValue === "All" ? ["All"] : [];
          } else {
            initial[p.name] = p.defaultValue || "";
          }
        } else {
          initial[p.name] = p.defaultValue ?? "";
        }
      });

      setReportConfig({ id: rptId, parameters: params, meta: rptMeta   });
      setFormValues(initial);

      // Build dropdown options from Table3 (per your choice A)
      const table3 = resp?.Table3 || [];
      // For each dropdown param, create options using primeValue or userValue or fallback to the first key
    // Build dropdown options using matching table
const tables = [resp.Table3, resp.Table4, resp.Table5, resp.Table6].filter(Boolean);

params.forEach((p) => {
  if (p.controlType?.toLowerCase() !== "dropdown") return;

  setLoadingDropdowns((s) => ({ ...s, [p.name]: true }));

  const prime = p.primeValue;
  const user = p.userValue;

  let matchedTable = null;

  // FIND TABLE THAT CONTAINS BOTH COLUMNS
  for (const tbl of tables) {
    if (!tbl || tbl.length === 0) continue;

    const firstRow = tbl[0];
    const hasPrime = prime && Object.prototype.hasOwnProperty.call(firstRow, prime);
    const hasUser = user && Object.prototype.hasOwnProperty.call(firstRow, user);

    if (hasPrime || hasUser) {
      matchedTable = tbl;
      break;
    }
  }

  // If still not found, fallback to first table
  if (!matchedTable) matchedTable = tables[0] || [];

  // Create dropdown options
  const opts = matchedTable.map((row) => ({
    value: row[prime] ?? row[user] ?? Object.values(row)[0],
    label: row[user] ?? row[prime] ?? Object.values(row)[0],
  }));

  // Insert ALL
  const dedup = [];
  const seen = new Set();
 // dedup.push({ value: "All", label: "All" });

  for (const o of opts) {
    const val = String(o.value);
    if (!seen.has(val)) {
      seen.add(val);
      dedup.push({
        value: val,
        label: String(o.label),
      });
    }
  }

  setDropdownOptions((prev) => ({ ...prev, [p.name]: dedup }));
  setLoadingDropdowns((s) => ({ ...s, [p.name]: false }));
});

    } catch (err) {
      console.error("Failed to load report parameters", err);
    } finally {
      setLoadingParams(false);
    }
  };

    async function getUserIP() {
  try {
    const res = await fetch("https://api.ipify.org/?format=json");
    const data = await res.json();
    return data.ip;  
  } catch {
    return "";
  }
}
  const updateFormValue = (name, val) => {
    setFormValues((prev) => ({ ...prev, [name]: val }));
  };

const prepareReport = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  let validationErrors = [];
  reportConfig.parameters.forEach((param) => {
    if (param.controlType?.toLowerCase() === "dropdown") {
      const val = formValues[param.name];
      if (param.isMultiSelect === 1) {
        if (!val || val.length === 0) {
          validationErrors.push(`Please select at least one value for ${param.title}`);
        }
      } else {
        if (!val || val === "") {
          validationErrors.push(`Please select a value for ${param.title}`);
        }
      }
    }
  });
  if (validationErrors.length > 0) {
    alert(validationErrors.join('\n'));
    setSubmitting(false);
    return;
  }

  try {
    const userIP = await getUserIP();
    const user = getCurrentUser();
    const sessId = getSessId();
    const databaseKey = sessionStorage.getItem("databaseKey") || "";

    const paramParts = [];
    const userParamListArray = [];
    (reportConfig?.parameters || []).forEach((param) => {
      const rawValue = formValues[param.name];
      let apiValue = "";

      if (param.controlType?.toLowerCase() === "dropdown") {
        if (Array.isArray(rawValue)) {
          apiValue = rawValue.includes("All") ? "All" : rawValue.join(",");
        } else {
          apiValue = rawValue ?? "";
        }
      } else {
        apiValue = rawValue ?? "";
      }

      paramParts.push(` ${param.name} ='${apiValue}'`);

      // For userParamList, use display value (label) instead of apiValue
      let displayValue = apiValue;
      if (param.controlType?.toLowerCase() === "dropdown") {
        const opts = dropdownOptions[param.name] || [];
        if (Array.isArray(rawValue)) {
          if (rawValue.includes("All")) {
            displayValue = "All";
          } else {
            displayValue = rawValue.map(v => {
              const opt = opts.find(o => o.value == v);
              return opt ? opt.label : v;
            }).join(", ");
          }
        } else {
          const opt = opts.find(o => o.value == rawValue);
          displayValue = opt ? opt.label : rawValue;
        }
      }

      if (displayValue) {
        userParamListArray.push({paramTitle: param.title, value: displayValue});
      }
    });

    const userParamListString = " select Input List \\ " + userParamListArray.map(item => `${item.paramTitle}=${item.value}`).join(' \\ ');
    setUserParamList(userParamListString);

    paramParts.push(` @Usrname ='${user}'`);
    paramParts.push(` @SessId ='${sessId}'`);

    const paramString = paramParts.join(" ,");

   const payload = {
  item: {
    rptid: Number(selectedReportId),
    mdlid: String(selectedReportId),
    procName: reportConfig?.meta?.PRoceName || "",
    paramandvalues: paramString,
    userParamList: userParamListString,
    userName: user,
    isExcelNeed: 1
  },
  log: {
    frontEndServerIP: window.location.hostname,
    userServerIP:  userIP,
    moduleName: "Advance Rmp Report" ,
    functionName: "AdvRptGenerate",
    primeId:"0",
    mdlId: String(selectedReportId),
    primeIdColValue:"CallId",
    moduleColName: "ReportName",
    browserInfo: navigator.userAgent,
    moduleValue: reportConfig?.meta?.Reportname || ""
  }
};

    const result = await api.AdvRptCreateCallId(payload);
    console.log("API RESPONSE:", result);

    setReportData(result.finalData);
    setOutputModal({ show: true });

  } catch (err) {
    console.error("Error generating report:", err);
    alert("Failed to generate report!");
  }

  setSubmitting(false);
};

  // AI Chat handlers
  const handleAiSend = async () => {
    if (!aiInputText.trim() && aiAttachments.length === 0) return;

 const userMessage = {
  role: 'user',
  content: aiInputText,
  attachments: aiAttachments.map(f => f.name),
  timestamp: new Date().toLocaleString()
};
    setAiMessages(prev => [...prev, userMessage]);
    setAiInputText("");
    setAiAttachments([]);
    setAiIsLoading(true);

    try {
      const bodyData = {
        user_query: aiInputText,
        json_data: reportData
      };
      if (aiAttachments.length > 0) {
        bodyData.attachments = aiAttachments.map(f => f.name);
      }

      let aiBaseUrl;
      if (selectedAiEndpoint === "AI_Gemini_URL") {
        aiBaseUrl = await getAiGeminiUrl();
      } else if (selectedAiEndpoint === "AI_Revamp_URL") {
        aiBaseUrl = await getAiRevampUrl();
      } else {
        aiBaseUrl = await getAiBaseUrl();
      }
      const response = await fetch(`${aiBaseUrl}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData)
      });

      const data = await response.json();

      if (data.report_html) {
        // Store report HTML in sessionStorage and open in popup
     sessionStorage.setItem("aiReportHtml", data.report_html);

const aiMessage = {
  role: "assistant",
  content: `
AI report ready!  
👉 <a href="#" class="text-purple-600 underline" onclick="window.open('/ai-report-display','aiReportPopup','width=1200,height=800,scrollbars=yes,resizable=yes');">View AI Result</a>
  `,
  timestamp: new Date().toLocaleString()
};
        setAiMessages(prev => [...prev, aiMessage]);
      } else {
        const aiMessage = {
          role: 'assistant',
          content: data.response || "No response"
        };
        setAiMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error('AI API error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "Sorry, I couldn't process your request. Please try again."
      };
      setAiMessages(prev => [...prev, errorMessage]);
    } finally {
      setAiIsLoading(false);
    }
  };
    const [permissions, setPermissions] = useState({});

  useEffect(() => {
  const loadPermissions = async () => {
    try {
      const perms = await getPermissions();
      setPermissions(perms);
    } catch (err) {
      console.error("Failed to load permissions", err);
    }
  };
  loadPermissions();
}, []);

  const handleAiKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSend();
    }
  };

  const handleAiFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAiAttachments(prev => [...prev, ...files]);
  };

  const startAiVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice recognition not supported in this browser');
      return;
    }

    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setAiIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setAiInputText(prev => prev + transcript);
    };

    recognition.onend = () => {
      setAiIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setAiIsListening(false);
    };

    recognition.start();
  };

  return (
    <div className="h-full p-6 mx-auto shadow-lg max-w-7xl rounded-xl md:p-8" style={{ backgroundColor: 'var(--base-100)', borderColor: 'var(--base-300)' }}>
      <h2 className="mb-4 text-2xl font-bold text-gray-800 md:text-3xl">Advance Reports</h2>
   

      {/* Report selection */}
      {loadingReports ? (
        <div className="py-8 text-center text-gray-500">
          <Loader className="mx-auto mb-2 animate-spin" size={24} />
          Loading reports...
        </div>
      ) : (
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">Select Report</label>
          <SearchableSelect
            options={reportList.map((r) => ({ value: r.id, label: `${r.name}` }))}
            value={selectedReportId}
            onChange={(val) => handleReportSelect(val)}
            placeholder="Select a report..."
            isLoading={loadingReports}
            autoFocus={true}
          />
        </div>
      )}

      {/* Parameters */}
      {loadingParams && (
        <div className="py-8 text-center text-gray-500">
          <Loader className="mx-auto mb-2 animate-spin" size={24} />
          Loading parameters...
        </div>
      )}

      {reportConfig && !loadingParams && (
        <form onSubmit={prepareReport} className="space-y-6">
          <h3 className="pb-2 text-xl font-semibold text-gray-800 border-b">Parameters</h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reportConfig.parameters.map((p) => (
              <div key={p.slNo} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{p.title}</label>

                {p.controlType?.toLowerCase() === "dropdown" ? (
                  p.isMultiSelect === 1 ? (
                    <ModalMultiSelect
                      options={dropdownOptions[p.name] || []}
                      value={formValues[p.name]}
                      onChange={(v) => updateFormValue(p.name, v)}
                      title={`Select ${p.title}`}
                      isLoading={loadingDropdowns[p.name]}
                    />
                  ) : (
                    <SearchableSelect
                      options={dropdownOptions[p.name] || []}
                      value={formValues[p.name]}
                      onChange={(v) => updateFormValue(p.name, v)}
                      placeholder={`Select ${p.title}...`}
                      isLoading={loadingDropdowns[p.name]}
                    />
                  )
                ) : p.controlType?.toLowerCase() === "datetime" ? (
                  <input
                    type="date"
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formValues[p.name] || ""}
                    onChange={(e) => updateFormValue(p.name, e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formValues[p.name] || ""}
                    onChange={(e) => updateFormValue(p.name, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              className="px-6 py-2 text-sm font-medium transition-colors shadow-sm btn-professional-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader className="inline mr-2 animate-spin" size={16} />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </button>
          </div>
        </form>
      )}
{outputModal.show && (
  <div className="fixed inset-0 z-50 flex" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
  <div className="flex flex-col w-full min-h-screen shadow-2xl"
     style={{ backgroundColor: 'var(--base-100)' }}>

      <div className="flex items-center p-1 border-b bg-gray-50" style={{ borderColor: 'var(--base-300)', backgroundColor: 'var(--base-200)' }}>
        <h3 className="flex-1 text-lg gray-800 font--semibold text">{reportData?.Table?.[0]?.ReportTitle || ''}</h3>
        <div className="flex items-center justify-center flex-1 gap-3">
          <button className="p-2 text-white transition-colors bg-green-600 rounded-md hover:bg-green-700" title="Download Excel" onClick={() => window.open(reportData?.Table?.[0]?.RevXlUrlPath, '_blank')}>
            <FileSpreadsheet size={18} />
          </button>
          <button
            className="p-2 text-white transition-colors bg-blue-600 rounded-md hover:bg-blue-700"
            title="View HTML"
            onClick={() => {
              let htmlContent = reportData?.Table?.[0]?.HTMlString || "";

              // Collect dynamic tables
              const dynamicJSON = {};
              Object.keys(reportData).forEach(key => {
                if (key.startsWith("Table")) {
                  dynamicJSON[key] = reportData[key];
                }
              });

              // Base64 encode JSON (prevents ALL syntax errors)
              const encodedJSON = btoa(unescape(encodeURIComponent(JSON.stringify(dynamicJSON))));

              // JS to decode it back
              const jsonScript = `
                  const tableData = JSON.parse(decodeURIComponent(escape(atob("${encodedJSON}"))));
              `;

              // Inject into HTML
              htmlContent = htmlContent.replace("<!--RMPJSONDATA--!>", jsonScript);

              const blob = new Blob([htmlContent], { type: "text/html" });
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank");
              URL.revokeObjectURL(url);
            }}
          >
            <Eye size={18} />
          </button>
            {permissions?.aichat === 1 && (
  <button
    className={`p-2 rounded-md transition-colors ${
      showAiChat ? 'bg-purple-700' : 'bg-purple-600'
    } text-white hover:bg-purple-700`}
    title="AI Assistant"
    onClick={() => setShowAiChat(!showAiChat)}
  >
    <Bot size={18} />
  </button>
)}
        </div>
        <div className="flex justify-end flex-1">
          <button className="p-2 text-white transition-colors bg-red-600 rounded-md hover:bg-red-700" title="Close" onClick={() => {setOutputModal({show:false}); setReportData(null); setShowAiChat(false);}}>
            ×
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Report Content */}
        <div className={`flex-1 overflow-y-auto p-2 transition-all duration-300 ${showAiChat ? 'lg:pr-2' : ''}`}>
           <div className="mb-1">
            <label className="block mb-1 text-sm font-medium text-gray-700">User Parameters</label>
            <pre className="p-1 overflow-x-auto text-sm text-gray-800 border border-gray-200 rounded-md bg-gray-50 max-h-20">{userParamList}</pre>
          </div>

          <div className="overflow-x-auto">
            <TabsComponent tables={reportData} />
          </div>
        </div>

        {/* AI Chat Side Panel */}
        <div className={`fixed lg:relative top-0 right-0 h-full border-l flex flex-col transition-all duration-300 ease-in-out ${
          showAiChat ? 'w-full lg:w-96 translate-x-0' : 'w-0 translate-x-full lg:translate-x-0'
        } ${showAiChat ? 'z-10' : 'z-0'}`} style={{ backgroundColor: 'var(--base-200)', borderColor: 'var(--base-300)' }}>
          {showAiChat && (
            <>
              <div className="p-4 border-b shadow-sm" style={{ borderColor: 'var(--base-300)', backgroundColor: 'var(--base-100)' }}>
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-purple-600">
                    <Bot size={20} />
                    AI Assistant
                  </h3>
                  <button
                    onClick={() => setShowAiChat(false)}
                    className="text-xl text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="flex flex-col flex-1 p-4 overflow-hidden">
                {aiAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 mb-4 border border-blue-200 rounded-lg bg-blue-50">
                    {aiAttachments.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-1 text-sm bg-white border border-blue-300 rounded-md">
                        <span className="truncate max-w-32">{file.name}</span>
                        <button
                          onClick={() => setAiAttachments(prev => prev.filter((_, i) => i !== idx))}
                          className="text-lg text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
<div className="flex items-end gap-3 mb-4">
                  <button
                    onClick={() => aiFileInputRef.current?.click()}
                    className="p-2 text-gray-500 transition-all duration-200 border border-gray-200 rounded-lg hover:text-purple-500 hover:bg-purple-50 hover:border-purple-300"
                    title="Attach files"
                  >
                    <Plus size={18} />
                  </button>
                  <input
                    ref={aiFileInputRef}
                    type="file"
                    multiple
                    onChange={handleAiFileSelect}
                    className="hidden"
                  />
                  <div className="flex-1">
                   <textarea
  value={aiInputText}
  onChange={(e) => setAiInputText(e.target.value)}
  onKeyDown={handleAiKeyPress}
  placeholder="Ask anything about your report..."
  className="w-full px-3 py-2 text-sm leading-relaxed transition-all duration-200 bg-white border border-gray-300 rounded-lg shadow-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
  rows={3}
  style={{
    minHeight: "80px",
    maxHeight: "120px",
    fontSize: "14px"
  }}
/>
                  </div>
                  <div className="flex flex-col gap-2">
                    <select
                      value={selectedAiEndpoint}
                      onChange={(e) => setSelectedAiEndpoint(e.target.value)}
                      className="px-2 py-1 text-xs font-medium transition-all duration-200 bg-white border border-gray-300 rounded appearance-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      title="Select AI Endpoint"
                    >
                      <option value="AI_BASE_URL">Base AI</option>
                      <option value="AI_Gemini_URL">💎 Gemini</option>
                      <option value="AI_Revamp_URL">🔄 Revamp</option>
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={startAiVoiceRecognition}
                        className={`p-2 rounded-lg transition-all duration-200 border ${
                          aiIsListening
                            ? 'bg-red-500 text-white border-red-500 animate-pulse'
                            : 'text-gray-500 hover:text-purple-500 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                        }`}
                        title="Voice input"
                      >
                        <Mic size={16} />
                      </button>
                      <button
                        onClick={handleAiSend}
                        disabled={aiIsLoading || (!aiInputText.trim() && aiAttachments.length === 0)}
                        className="p-2 text-white transition-all duration-200 bg-purple-500 rounded-lg shadow-sm hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Send"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-4 space-y-3 overflow-y-auto border rounded-lg shadow-inner" style={{ backgroundColor: 'var(--base-100)', borderColor: 'var(--base-300)' }}>
                  {aiMessages.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                      <Bot size={32} className="mx-auto mb-2 text-purple-500" />
                      <p className="text-sm font-medium">How can I help you with your report data?</p>
                      <p className="mt-1 text-xs text-gray-400">Enter your query above and get AI-powered insights</p>
                    </div>
                  )}
                  {aiMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm shadow-sm transition-all duration-200 ${
                        msg.role === 'user'
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 border border-gray-200 text-gray-800'
                      }`}>
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="pb-1 mb-2 text-xs border-b border-gray-300 opacity-75">
                            📎 {msg.attachments.join(', ')}
                          </div>
                        )}
                        {msg.timestamp && (
                          <div className="text-[10px] opacity-60 mb-1">
                            🕒 {msg.timestamp}
                          </div>
                        )}
                        <div
                          className="text-sm leading-relaxed whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: msg.content }}
                        />
                      </div>
                    </div>
                  ))}
                  {aiIsLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 border border-gray-200 px-3 py-2 rounded-lg shadow-sm animate-pulse max-w-[85%]">
                        <div className="flex items-center space-x-2">
                          <Loader className="animate-spin" size={14} />
                          <span className="text-xs text-gray-600">AI is analyzing your data...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={aiMessagesEndRef} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  </div>
)}


</div>
);


};


// -----------------------------
// Page Wrapper (login-style background)
// -----------------------------
const AdvanceReport = () => {
  const navigate = useNavigate();
  
  // ESC key handler to go back to explorer
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        navigate("/explorer");
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [navigate]);
  
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--base-200)' }}
    >
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-20 flex items-center justify-between p-4 shadow-lg" style={{ backgroundColor: 'var(--base-100)', borderBottom: '1px solid var(--base-300)' }}>
        <button onClick={() => navigate("/explorer")} className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200">
          ← Back to Explorer
        </button>
        <h1 className="text-xl font-bold text-gray-800">Advance Report Generator</h1>
        <div></div> {/* Empty for balance */}
      </div>

      <div className="pt-20 px-4 md:px-8 pb-8 min-h-[calc(100vh-5rem)]">
        <ReportGenerator />
      </div>
    </div>
  );
};

export default AdvanceReport;

 